import * as Schema from 'generated/graphql/schema';
import * as React from 'react';

import InputValidator from '@/components/validators-forms/input-validator';
import WrapRequiredFieldsDescriptionComponent from '@/views/common-components/wrap-required-fileds-description';
import { SensorSettingsState } from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

import { TabPanelComponent, TabPropsType } from '.';

const isShown = (state: SensorSettingsState) => {
  const type = state?.config?.sensor?.type;
  return (
    state.peripheralType === 'SENSOR' &&
    !!type &&
    (type.startsWith('COUNTER') || type === Schema.SensorType.MANUAL_PROCESS)
  );
};

const round = (value: number, decimals: number) => {
  const num = Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
  return !isNaN(num) && num > 0 ? num : 0;
};

export const TabProps: TabPropsType = {
  isShown: (s) => isShown(s),
  isValid: (state) =>
    state?.config?.sensor?.validatedSpeed !== undefined &&
    state?.config?.sensor?.validatedSpeed !== null &&
    state?.config?.sensor?.expectedSpeed !== undefined &&
    state?.config?.sensor?.expectedSpeed !== null &&
    state?.config?.sensor?.validatedSpeed >= state?.config?.sensor?.expectedSpeed &&
    state?.config?.sensor?.expectedSpeed > 0,
  label: (t) => t('device:speeds', { defaultValue: 'Speeds' }),
};

export const TabPanel: TabPanelComponent = ({ handleChange, state, invalidFormMessage, t, classes }) => {
  const sensorValidatedSpeedRef = React.useRef<InstanceType<typeof InputValidator> | null>(null);
  const sensorExpectedSpeedRef = React.useRef<InstanceType<typeof InputValidator> | null>(null);

  if (!isShown(state)) {
    return null;
  }

  const onValidatedSpeedChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        validatedSpeed: parseFloat(val),
      },
    });

    sensorExpectedSpeedRef.current?.validate(state.config?.sensor?.expectedSpeed);
  };

  const onExpectedSpeedChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    let val = e.target.value;
    if (val === '-') {
      val += '0';
    }
    handleChange({
      sensor: {
        expectedSpeed: parseFloat(val),
        validatedSpeed: state.config?.sensor?.validatedSpeed,
      },
    });

    sensorValidatedSpeedRef.current?.validate(state.config?.sensor?.validatedSpeed);
  };

  return (
    <div className={classes.tabPanel}>
      <InputValidator
        ref={sensorValidatedSpeedRef}
        className={classes.textFields}
        containerClass={classes.containerClass}
        name="validatedSpeed"
        type="number"
        label={t(['device:validatedSpeedInPcsPerMinute'], {
          defaultValue: 'Validated speed in pcs per minute',
        })}
        validators={['required', 'isFloat', 'minNumber:' + state?.config?.sensor?.expectedSpeed]}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
          t(['shared:mustNotBeLessThanExpectedSpeed'], { defaultValue: 'must not be less than expected speed' }),
        ]}
        value={state?.config?.sensor?.validatedSpeed}
        helperText={
          !!state?.config?.sensor?.validatedSpeed && state?.config?.sensor?.validatedSpeed > 0
            ? t(['device:configurationValidatedSpeedExplanation'], {
                defaultValue: 'The equipment is validated to run at {{validatedSpeed}} sec/pcs',
                validatedSpeed: round(60 / (state?.config?.sensor?.validatedSpeed || 200), 2),
              })
            : ''
        }
        onChange={onValidatedSpeedChange}
      />
      <br />
      <br />
      <InputValidator
        ref={sensorExpectedSpeedRef}
        className={classes.textFields}
        containerClass={classes.containerClass}
        type="number"
        name="expectedSpeed"
        label={t(['device:expectedSpeedInPcsPerMinute'], {
          defaultValue: 'Expected speed in pcs per minute',
        })}
        validators={['required', 'isFloat', 'minNumber:0.00001', 'maxNumber:' + state?.config?.sensor?.validatedSpeed]}
        errorMessages={[
          t(['shared:fieldIsRequired'], { defaultValue: 'this field is required' }),
          t(['shared:mustBePositive'], { defaultValue: 'must be a positive number' }),
          t(['shared:mustNotBeZero'], { defaultValue: 'must not be zero' }),
          t(['shared:mustNotBeGreaterThanValidatedSpeed'], {
            defaultValue: 'must not be greater than validated speed',
          }),
        ]}
        value={state?.config?.sensor?.expectedSpeed}
        helperText={t(['batches:expectedSpeedHelp'], {
          defaultValue: 'The equipment is expected to run at {{expectedSpeed}} sec/pcs',
          expectedSpeed: round(60 / (state?.config?.sensor?.expectedSpeed ?? 200), 2),
        })}
        onChange={onExpectedSpeedChange}
      />
      {invalidFormMessage}
      <br />
      <WrapRequiredFieldsDescriptionComponent />
    </div>
  );
};
